import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { Lang } from '@/data/types';
import { KBIcon } from '@/components/kbIcons';
import { actesBoard, actesQueue, actesUploadFile, type ActesBoard, type ActesQueue, type ActesQueueRow, type ActesReference } from '@/integrations/actesApi';
import { setActeOpen } from '@/lib/actesOpen';
import { DocViewer } from './DocViewer';
import { ActesAvatar } from './ActesPerson';
import { pillOf } from './pill';
import { countryOfRef, dossierStatusLabel, actesEn } from '@/lib/actesCountry';
import { currentActesCountry } from '@/integrations/actesApi';

/**
 * TABLEAU DE BORD — le port EXACT de la maquette v7, section #dashboard.
 *
 * L'ordre, les blocs et les classes viennent de la maquette et de nulle part
 * ailleurs : `.section-t`, `.stats`/`.stat`, `.grid-2`/`.col`, `.card`/`.card-h`,
 * `.q`, `.dlist`, `.ag`, `.recs`. Ce qui change ici est PUREMENT le marquage :
 * les requêtes, les compteurs et les destinations sont ceux d'avant.
 *
 * CE QUI A ÉTÉ RETIRÉ, ET POURQUOI : les cartes pliables. La maquette n'a pas
 * de pli, et la consigne est claire — ce que la maquette ne prévoit pas ne
 * reste pas. Le réglage rangé dans le navigateur ne sert donc plus.
 */
export function BoardPanel({ lang, reference, onOpenDossier, onGo }: {
  lang: Lang;
  reference: ActesReference;
  onOpenDossier: (id: string) => void;
  onGo: (tab: 'dossiers' | 'demandes' | 'reception' | 'dpc', view?: 'scans') => void;
}) {
  const T = (fr: string, en: string) => (lang === 'fr' ? fr : actesEn(en));

  const queue = useQuery<ActesQueue>({
    queryKey: ['actes', 'queue', reference.me.id],
    queryFn: ({ signal }) => actesQueue(signal),
    staleTime: 60_000,
    retry: false,
  });

  const q = useQuery<ActesBoard>({
    queryKey: ['actes', 'board', reference.me.id],
    queryFn: ({ signal }) => actesBoard(signal),
    staleTime: 60_000,
  });

  /* Voir le document sans quitter le tableau de bord, et le télécharger. */
  const [see, setSee] = useState<null | (() => Promise<{ url: string; downloadUrl: string; name: string | null; mime: string | null }>)>(null);
  const download = async (id: string, name: string) => {
    try {
      const f = await actesUploadFile(id);
      const a = document.createElement('a');
      a.href = f.downloadUrl || f.url;
      a.download = f.name || name;
      document.body.appendChild(a); a.click(); a.remove();
    } catch { /* le lien est une courtoisie : s'il casse, la fiche reste */ }
  };

  const n = (v: string | number | undefined) => Number(v ?? 0);
  const c = q.data?.counts;
  const mesEnAttente = n(c?.mes_actes) - n(c?.mes_actes_recus);
  const enAttente = n(c?.actes) - n(c?.actes_recus);
  const pct = (a: number, b: number) => (b > 0 ? Math.max(0, Math.min(100, Math.round((a / b) * 100))) : 0);

  /* « today », « yest. », puis la date — une liste d'arrivées se lit par
     quand, pas par une date répétée. */
  const when = (iso: string) => {
    const diff = (Date.now() - new Date(iso).getTime()) / 86400_000;
    if (diff < 1) return T('auj.', 'today');
    if (diff < 2) return T('hier', 'yest.');
    return new Date(iso).toLocaleDateString(lang === 'fr' ? 'fr-FR' : 'en-GB', { day: '2-digit', month: 'short' });
  };
  const days = (iso: string | null): number | null => {
    if (!iso) return null;
    const d = new Date(`${iso}T00:00:00Z`).getTime();
    return Number.isNaN(d) ? null : Math.round((d - Date.now()) / 86_400_000);
  };
  /* La maquette écrit « 5 weeks » à droite d'un acte qui périme, et
     « 64 days » à droite d'un acte parti. Deux mesures, deux mots. */
  const weeksLeft = (iso: string | null): string => {
    const d = days(iso);
    if (d === null) return '—';
    if (d < 0) return T(`échu · ${-d} j`, `expired · ${-d} d`);
    if (d <= 13) return T(`${d} j`, `${d} d`);
    return T(`${Math.round(d / 7)} sem.`, `${Math.round(d / 7)} weeks`);
  };
  const daysOut = (iso: string | null): string => {
    const d = days(iso);
    return d === null ? '—' : T(`${-d} j`, `${-d} days`);
  };
  const dayShort = (iso: string | null) => (iso
    ? new Date(`${iso}T00:00:00Z`).toLocaleDateString(lang === 'fr' ? 'fr-FR' : 'en-GB', { day: '2-digit', month: 'short', timeZone: 'UTC' })
    : null);

  /* Le donut de la maquette, au trait près : r=14 sur 34 px, la piste en
     --bg-2, l'arc en --sage, et en --terra quand le dossier est en retard. */
  const donut = (p: number, late: boolean) => {
    const circ = 2 * Math.PI * 14;
    return (
      <span className="donut">
        <svg width="34" height="34" viewBox="0 0 34 34" aria-hidden="true">
          <circle cx="17" cy="17" r="14" fill="none" stroke="var(--bg-2)" strokeWidth="4" />
          <circle cx="17" cy="17" r="14" fill="none" stroke={late ? 'var(--terra)' : 'var(--sage)'} strokeWidth="4"
            strokeDasharray={circ} strokeDashoffset={circ * (1 - p / 100)}
            transform="rotate(-90 17 17)" strokeLinecap="round" />
        </svg>
        <span>{p}%</span>
      </span>
    );
  };

  if (q.isLoading) return <p className="muted-note">{T('Chargement…', 'Loading…')}</p>;

  const qd = queue.data;
  const mine = q.data?.mine ?? [];
  const latest = q.data?.latest ?? [];
  /* Le libellé d'une ligne de file : la personne, puis le type. */
  const kindOf = (r: ActesQueueRow) => (countryOfRef(reference) === 'PT'
    /* Portugal: what was asked for, in its own words, or the type in English. */
    ? r.request_line || r.type_label_en || 'Document'
    : r.label_short_fr || r.type_label_fr || T('Acte', 'Record'));

  return (
    <section className="actes-panel v7">
      <div className="section-t">
        <h2>{T('Sur vous', 'On you')}</h2>
        <p>{mesEnAttente > 0
          ? T(`${mesEnAttente} documents attendus sur vos dossiers.`, `${mesEnAttente} documents still expected on your dossiers.`)
          : T('Rien ne vous attend sur vos dossiers.', 'Nothing is waiting on your dossiers.')}</p>
      </div>

      <div className="stats">
        <button type="button" className="stat" onClick={() => onGo('dossiers')}>
          <span className="ic"><KBIcon name="documents" size={14} /></span>
          <div className="v num">{n(c?.mes_dossiers)}</div>
          <div className="k">{T('Mes dossiers', 'My dossiers')}</div>
          <div className="m">{T('dont vous êtes référent ou équipe', 'where you are lead or team')}</div>
        </button>
        <button type="button" className={`stat ${mesEnAttente > 0 ? 'warn' : ''}`} onClick={() => onGo('demandes')}>
          <span className={`ic ${mesEnAttente > 0 ? 'warn' : ''}`}><KBIcon name="clock" size={14} /></span>
          <div className="v num">{mesEnAttente}</div>
          <div className="k">{T('En attente', 'Still pending')}</div>
          <div className="m">{T(`sur ${n(c?.mes_actes)} demandes à vous`, `of ${n(c?.mes_actes)} requests of yours`)}</div>
        </button>
        <button type="button" className={`stat ${n(c?.mes_expirations) > 0 ? 'bad' : ''}`} onClick={() => onGo('demandes')}>
          <span className={`ic ${n(c?.mes_expirations) > 0 ? 'bad' : ''}`}><KBIcon name="bell" size={14} /></span>
          <div className="v num">{n(c?.mes_expirations)}</div>
          <div className="k">{T('Expirent bientôt', 'Expiring soon')}</div>
          <div className="m">{T('actes qui passent leurs trois mois avant le dépôt', 'records that pass three months before you can file')}</div>
        </button>
        {/* Portugal has no DPC: its fourth figure is what already came in. */}
        {countryOfRef(reference) === 'PT' ? (
          <button type="button" className="stat ok" onClick={() => onGo('demandes')}>
            <span className="ic"><KBIcon name="check" size={14} /></span>
            <div className="v num">{n(c?.mes_actes_recus)}</div>
            <div className="k">Received</div>
            <div className="m">documents already in on your requests</div>
          </button>
        ) : (
          <button type="button" className={`stat ${n(c?.dpc_a_venir) > 0 ? 'bad' : ''}`} onClick={() => onGo('dpc')}>
            <span className={`ic ${n(c?.dpc_a_venir) > 0 ? 'bad' : ''}`}><KBIcon name="calendar" size={14} /></span>
            <div className="v num">{n(c?.dpc_a_venir)}</div>
            <div className="k">{T('DPC à échéance', 'DPC due')}</div>
            <div className="m">{T('échéances judiciaires à venir', 'court deadlines coming up')}</div>
          </button>
        )}
      </div>

      <div className="grid-2" style={{ marginTop: 18 }}>
        <div className="col">
          {/* ── Needs you today ──
              La maquette met ici la file de triage. La pile qui appelle un
              geste aujourd'hui est celle des demandes JAMAIS parties sur un
              dossier ouvert : rien n'a été commandé, personne n'attend. */}
          {!!qd?.never.length && (
            <div className="card">
              <div className="card-h">
                <h3>{T('Ce qui vous attend aujourd’hui', 'Needs you today')}</h3>
                <span className="c mono">{qd.never.length}</span>
                <button type="button" className="link" onClick={() => onGo('demandes')}>
                  {T('Toutes mes demandes', 'All my requests')}
                </button>
              </div>
              <ul className="q">
                {qd.never.slice(0, 6).map((r) => (
                  <li key={r.id}>
                    <i className="spine now" />
                    <div className="b">
                      <div className="l1">
                        <b>{r.person_concerned}</b>
                        {r.dossier_name && <span className="sub">{r.dossier_name}</span>}
                      </div>
                      <div className="l2">
                        <span>{kindOf(r)}</span>
                        <span className="dash">·</span>
                        <span>{T('sur un dossier ouvert, jamais demandé', 'on an open dossier, never requested')}</span>
                      </div>
                    </div>
                    {/* On ouvre LA DEMANDE, pas le dossier : c'est elle qui
                        attend un geste, et la chercher dans une liste de
                        vingt-huit était le geste de trop. */}
                    <button type="button" className="btn btn-s"
                      onClick={() => { setActeOpen(r.id); onGo('demandes'); }}>
                      {T('Ouvrir la demande', 'Open the request')}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="card">
            <div className="card-h">
              <h3>{T('Mes dossiers', 'My dossiers')}</h3>
              <span className="c mono">{mine.length}</span>
              <button type="button" className="link" onClick={() => onGo('dossiers')}>
                {T('Tous les dossiers →', 'All dossiers →')}
              </button>
            </div>
            {mine.length === 0 ? (
              <p className="muted-note" style={{ padding: '0.75rem 1rem' }}>
                {T('Aucun dossier ne vous est attribué.', 'No dossier is assigned to you.')}
              </p>
            ) : (
              <ul className="dlist rows">
                {mine.map((d) => {
                  const p = Math.round(Number(d.completion_pct) || 0);
                  const pend = Math.max(0, n(d.actes_total) - n(d.actes_received));
                  return (
                    <li key={d.id}>
                      <button type="button" className="row" onClick={() => onOpenDossier(d.id)}>
                        {donut(p, p < 50)}
                        <span className="nm">
                          <b>{d.name}</b>
                          <span>{d.actes_received}/{d.actes_total} {T('actes', 'records')}</span>
                        </span>
                        {d.status_label_fr && (
                          <span className={`pill ${pillOf(d.status_color)}`}>{dossierStatusLabel(reference, d)}</span>
                        )}
                        <span className={`blk ${pend > 0 ? 'late' : ''}`}>
                          {pend > 0 ? T(`${pend} manquants`, `${pend} still missing`) : T('complet', 'complete')}
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>

        <div className="col">
          {!!qd?.expiring.length && (
            <div className="card">
              <div className="card-h">
                <h3>{T('Expirent avant le dépôt', 'Expiring before you can file')}</h3>
                <span className="c mono">{qd.expiring.length}</span>
              </div>
              <ul className="ag rows">
                {qd.expiring.slice(0, 6).map((r) => {
                  const d = days(r.date_expiration);
                  return (
                    <li key={r.id}>
                      <button type="button" className="row"
                        onClick={() => { setActeOpen(r.id); onGo('demandes'); }}>
                      <span className="nm">
                        <b>{r.person_concerned} · {kindOf(r)}</b>
                        <span>{[r.authority_label_fr,
                          dayShort(r.date_demande) && dayShort(r.date_expiration)
                            ? `${dayShort(r.date_demande)} → ${dayShort(r.date_expiration)}`
                            : dayShort(r.date_expiration)].filter(Boolean).join(' · ')}</span>
                      </span>
                      <span className={`d ${d !== null && d <= 42 ? 'late' : ''}`}>{weeksLeft(r.date_expiration)}</span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}

          {!!qd?.sitting.length && (
            <div className="card">
              <div className="card-h">
                <h3>{T('Chez une autorité', 'Sitting with an authority')}</h3>
                <span className="c sub">{T('le plus ancien d’abord', 'longest first')}</span>
              </div>
              <ul className="ag rows">
                {qd.sitting.slice(0, 6).map((r) => {
                  const d = days(r.date_demande);
                  return (
                    <li key={r.id}>
                      <button type="button" className="row"
                        onClick={() => { setActeOpen(r.id); onGo('demandes'); }}>
                      <span className="nm">
                        <b>{kindOf(r)} — {r.person_concerned}</b>
                        <span>{r.authority_label_fr || T('sans autorité', 'no authority')}</span>
                      </span>
                      <span className={`d ${d !== null && d <= -30 ? 'late' : ''}`}>{daysOut(r.date_demande)}</span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}

          <div className="card">
            <div className="card-h">
              <h3>{T('Derniers actes reçus', 'Latest records in')}</h3>
              <span className="c mono">{latest.length}</span>
              <button type="button" className="link" onClick={() => onGo('demandes', 'scans')}>
                {T('Derniers scans →', 'Latest scans →')}
              </button>
            </div>
            {latest.length === 0 ? (
              <p className="muted-note" style={{ padding: '0.75rem 1rem' }}>
                {T('Rien n’est encore arrivé.', 'Nothing has come in yet.')}
              </p>
            ) : (
              /* SIX À L'ŒIL, DIX EN MAIN. Dix lignes d'affilée écrasaient la
                 colonne de droite et poussaient tout le reste hors de l'écran.
                 Six tiennent, et le reste se déroule dans la carte. */
              <ul className="recs rows scroll6">
                {latest.map((u) => (
                  <li key={u.id}>
                    <button type="button" className="row"
                      title={T('Ouvrir la demande', 'Open the request')}
                      onClick={() => {
                        /* La ligne est un acte reçu SUR une demande : c'est
                           elle qu'on ouvre. Sans demande — un scan pas encore
                           rattaché — on retombe sur le dossier. */
                        if (u.acte_request_id) { setActeOpen(u.acte_request_id); onGo('demandes'); }
                        else if (u.dossier_id) onOpenDossier(u.dossier_id);
                      }}>
                      <span className="t">{when(u.created_at)}</span>
                      <span className="nm">
                        <b>{u.person_concerned || u.file_name || T('Document', 'Document')}</b>
                        <span>{[u.detected_type, u.dossier_name || T('sans dossier', 'no dossier')].filter(Boolean).join(' · ')}</span>
                      </span>
                      {u.by_email && (
                        <ActesAvatar person={{ id: u.by_email, email: u.by_email, full_name: u.by_name, role: '', is_lawyer: false, is_active: true }} />
                      )}
                    </button>
                    {/* Voir et télécharger sans quitter le tableau de bord :
                        souvent la seule question est « c'est bien celui-là ? ». */}
                    <span className="recs-acts">
                      <button type="button" className="rb" title={T('Voir le document', 'View the document')}
                        onClick={() => setSee(() => () => actesUploadFile(u.id))}>
                        <KBIcon name="eye" size={13} />
                      </button>
                      <button type="button" className="rb" title={T('Télécharger', 'Download')}
                        onClick={() => void download(u.id, u.file_name || 'document')}>
                        <KBIcon name="download" size={13} />
                      </button>
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>

      <div className="section-t" style={{ marginTop: 26 }}>
        <h2>{T('Sur la France', currentActesCountry() === 'PT' ? 'Across Portugal' : 'Across France')}</h2>
        <p>{T('Toute la section, quel que soit le responsable.', 'The whole section, whoever owns it.')}</p>
      </div>
      <div className="stats">
        <button type="button" className="stat" onClick={() => onGo('dossiers')}>
          <span className="ic"><KBIcon name="documents" size={14} /></span>
          <div className="v num">{n(c?.dossiers)}</div>
          <div className="k">{T('Dossiers', 'Dossiers')}</div>
          <div className="m">{T('ouverts en ce moment', 'open right now')}</div>
        </button>
        <button type="button" className="stat" onClick={() => onGo('demandes')}>
          <span className="ic"><KBIcon name="services" size={14} /></span>
          <div className="v num">{n(c?.actes)}</div>
          <div className="k">{T('Demandes', 'Requests')}</div>
          <div className="m">{T('toutes demandes confondues', 'every request, all dossiers')}</div>
        </button>
        <button type="button" className="stat ok" onClick={() => onGo('demandes', 'scans')}>
          <span className="ic ok"><KBIcon name="check" size={14} /></span>
          <div className="v num">{n(c?.actes_recus)}</div>
          <div className="k">{T('Reçus', 'Received')}</div>
          <div className="m">{T(`${pct(n(c?.actes_recus), n(c?.actes))} % de l'ensemble`, `${pct(n(c?.actes_recus), n(c?.actes))}% of them`)}</div>
        </button>
        <button type="button" className="stat warn" onClick={() => onGo('demandes')}>
          <span className="ic warn"><KBIcon name="clock" size={14} /></span>
          <div className="v num">{enAttente}</div>
          <div className="k">{T('En attente', 'Pending')}</div>
          <div className="m">{T('rien n’est encore arrivé pour elles', 'nothing has come in for these yet')}</div>
        </button>
      </div>

      {see && <DocViewer lang={lang} load={see} onClose={() => setSee(null)} />}
    </section>
  );
}
